/**
 * Completion section — value target + unit (open, no switch), proof types.
 * Value is optional — if user fills target, it becomes required on check.
 * Premium gate: free users can only select "text" proof type.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Video, FileText, Gem } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { PremiumPage } from '../../../profile/components/premium-page'

interface HabitFormCompletionProps {
  valueTarget: string
  valueUnit: string
  proofTypes: string[]
  onValueTargetChange: (v: string) => void
  onValueUnitChange: (v: string) => void
  onProofTypesChange: (v: string[]) => void
}

const PROOF_OPTIONS = [
  { id: 'image', icon: Camera, labelKey: 'home.habitForm.photo', premiumOnly: true },
  { id: 'video', icon: Video, labelKey: 'home.habitForm.video', premiumOnly: true },
  { id: 'text', icon: FileText, labelKey: 'home.habitForm.text', premiumOnly: false },
] as const

export const HabitFormCompletion: React.FC<HabitFormCompletionProps> = ({
  valueTarget, valueUnit, proofTypes,
  onValueTargetChange, onValueUnitChange, onProofTypesChange,
}) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const isPremium = user?.is_premium === true
  const [showPremium, setShowPremium] = useState(false)

  const toggleProof = (id: string, locked: boolean): void => {
    if (locked) {
      setShowPremium(true)
      return
    }
    onProofTypesChange(
      proofTypes.includes(id) ? proofTypes.filter((p) => p !== id) : [...proofTypes, id],
    )
  }

  return (
    <>
      {/* Value target + unit — always visible, optional */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-2">
          {t('home.habitForm.valueLabel')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-text-hint/50 mb-1">
              {t('home.habitForm.valueTarget')}
            </label>
            <input type="number" min={1} value={valueTarget}
              onChange={(e) => onValueTargetChange(e.target.value)}
              className="w-full h-10 bg-white/5 border border-border rounded-xl px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary"
              placeholder="10" />
          </div>
          <div>
            <label className="block text-[10px] text-text-hint/50 mb-1">
              {t('home.habitForm.valueUnit')}
            </label>
            <input type="text" value={valueUnit}
              onChange={(e) => onValueUnitChange(e.target.value)}
              className="w-full h-10 bg-white/5 border border-border rounded-xl px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary"
              placeholder={t('home.habitForm.unitPlaceholder')} />
          </div>
        </div>
      </div>

      {/* Proof types */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-2">
          {t('home.habitForm.proofTypes')}
        </label>
        <div className="flex gap-2">
          {PROOF_OPTIONS.map(({ id, icon: Icon, labelKey, premiumOnly }) => {
            const isLocked = premiumOnly && !isPremium
            const isActive = proofTypes.includes(id)

            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleProof(id, isLocked)}
                disabled={isLocked}
                className={`relative flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all ${
                  isLocked
                    ? 'bg-white/[0.02] text-text-hint/25 border border-white/[0.04] cursor-not-allowed'
                    : isActive
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'bg-white/5 text-text-hint border border-border'
                }`}
              >
                <Icon size={14} />
                {t(labelKey)}
                {isLocked && (
                  <Gem size={11} className="text-primary/50 ml-0.5" />
                )}
              </button>
            )
          })}
        </div>
      </div>
      <PremiumPage isOpen={showPremium} onClose={() => setShowPremium(false)} />
    </>
  )
}
